import React, { useState } from 'react';
import { Modal, Button, Upload, message, Tooltip, Row, Col, Alert, Checkbox, Select } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useMutation, useLazyQuery } from '@apollo/client';
import { IMPORT_CUSTOMERS } from '../mutations/importCustomers';
import { GET_CUSTOMER_EXAMPLE_FILE } from '../mutations/customerExamples';

const CustomerImportModal = ({ visible, onCancel, refetchCustomers, resetToFirstPage }) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [updateDuplicated, setUpdateDuplicated] = useState(false);
  const [searchField, setSearchField] = useState('document'); // 'document' ou 'email'
  const [importResult, setImportResult] = useState(null); // Estado para controlar a exibição do resultado
  const [showResult, setShowResult] = useState(false); // Estado para mostrar/ocultar o resultado
  const [importCustomers] = useMutation(IMPORT_CUSTOMERS);
  const [getExampleFile, { loading: loadingExample }] = useLazyQuery(GET_CUSTOMER_EXAMPLE_FILE);

  const handleUpload = async () => {
    const file = fileList[0];
    if (!file) {
      message.error('Por favor, selecione um arquivo.');
      return;
    }

    setUploading(true);
    setErrors([]);
    try {
      const { data } = await importCustomers({
        variables: { 
          file: file.originFileObj,
          updateDuplicated: updateDuplicated,
          searchField: searchField
        },
        refetchQueries: ['GetCustomers'],
        awaitRefetchQueries: true
      });
      
      // Armazenar o resultado da importação
      setImportResult({
        message: data.importCustomers.message,
        errors: data.importCustomers.errors || [],
        hasErrors: data.importCustomers.errors && data.importCustomers.errors.length > 0
      });
      setShowResult(true);
      
      if (data.importCustomers.errors && data.importCustomers.errors.length > 0) {
        setErrors(data.importCustomers.errors);
      } else {
        // Limpar a seleção do arquivo após importação bem-sucedida
        
        
        // Resetar para primeira página se a função foi passada
        if (resetToFirstPage) {
          resetToFirstPage();
        }
        
        // Aguardar o refetch antes de fechar o modal
        if (refetchCustomers) {
          await refetchCustomers();
        }
      }
      setFileList([]);
    } catch (error) {
      message.error('Falha no upload: ' + (error.message || 'Erro desconhecido'));
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const downloadExampleFile = async () => {
    try {
      const { data } = await getExampleFile();
      
      if (data && data.customerExampleFile) {
        // Converter base64 em blob
        const byteCharacters = atob(data.customerExampleFile);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // Criar URL e fazer download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'customers_example.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        message.error('Erro ao obter o arquivo de exemplo');
      }
    } catch (error) {
      message.error('Falha ao baixar o arquivo de exemplo');
      console.error(error);
    }
  };

  const handleCloseModal = () => {
    setErrors([]);
    setFileList([]);
    setUpdateDuplicated(false);
    setSearchField('document');
    setImportResult(null);
    setShowResult(false);
    onCancel();
  };

  const handleCloseResult = () => {
    setShowResult(false);
    
    // Se não houve erros, fechar o modal completamente
    if (importResult && !importResult.hasErrors) {
      setFileList([]);
      setErrors([]);
      setImportResult(null);
      onCancel();
    } else {
      setImportResult(null);
    }
  };

  return (
    <Modal
      open={visible}
      title="Importar Clientes"
      onCancel={handleCloseModal}
      maskClosable={false}
      width={800}
      translate="no"
      className="notranslate"
      footer={[
        <Button key="cancel" onClick={handleCloseModal}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" onClick={handleUpload} disabled={fileList.length === 0} loading={uploading}>
          {uploading ? 'Enviando' : 'Enviar'}
        </Button>,
      ]}
    >
      <p>
        Importe seus clientes através de uma planilha Excel (.xlsx). A planilha deve conter as seguintes colunas:
      </p>
      <ul>
        <li><strong>Nome*</strong> - Nome completo do cliente</li>
        <li><strong>Documento</strong> - CPF ou CNPJ (opcional)</li>
        <li><strong>Telefone Principal*</strong> - Telefone de contato</li>
        <li><strong>Email</strong> - Endereço de email (opcional)</li>
        <li><strong>Descrição do Email</strong> - Ex: "Comercial", "Pessoal" (opcional)</li>
        <li><strong>CEP</strong> - Código postal (opcional)</li>
        <li><strong>Cidade</strong> - Código IBGE (numérico) ou nome da cidade (texto). Para busca por nome, o campo Estado deve estar preenchido</li>
        <li><strong>Estado</strong> - Código IBGE (numérico), nome completo ou sigla do estado (ex: "SP", "São Paulo")</li>
        <li><strong>Bairro</strong> - Nome do bairro (opcional)</li>
        <li><strong>Rua</strong> - Nome da rua (opcional)</li>
        <li><strong>Número</strong> - Número do endereço (opcional)</li>
        <li><strong>Data da Última Venda</strong> - Opcional, formato DD/MM/AAAA</li>
        <li><strong>Produto Favorito</strong> - Opcional, nome do produto favorito do cliente</li>
        <li><strong>Tags</strong> - Opcional, tags separadas por vírgula (ex: "VIP, Premium")</li>
        <li><strong>Data de Nascimento</strong> - Opcional, formato DD/MM/AAAA</li>
        <li><strong>Contato Principal</strong> - Opcional, informações adicionais sobre o contato principal do cliente</li>
      </ul>
      <p><strong>* Campos obrigatórios</strong></p>
      <p><strong>⚠️ Regras de Endereço:</strong></p>
      <ul>
        <li>Para cadastrar endereço, é necessário pelo menos a <strong>Cidade</strong></li>
        <li>Se a cidade for informada por <strong>nome</strong> (texto), o campo <strong>Estado</strong> é obrigatório</li>
        <li>Se a cidade for informada por <strong>código IBGE</strong> (numérico), o Estado é opcional</li>
        <li>Demais campos (CEP, Bairro, Rua, Número) são opcionais</li>
      </ul>

      <p><strong>Exemplos:</strong></p>
      <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
        <strong>Linha 1:</strong> Cliente completo com todos os dados<br/>
        <strong>Linha 2:</strong> Cliente com endereço completo mas sem data da última venda<br/>
        <strong>Linha 3:</strong> Cliente mínimo (apenas nome e telefone obrigatórios)
      </p>
      <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ backgroundColor: '#f0f0f0' }}>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Nome</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Documento</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Telefone</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Email</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Desc. Email</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>CEP</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Cidade</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Estado</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Bairro</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Rua</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Número</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Última Venda</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Produto Favorito</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Tags</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Data Nascimento</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Contato Principal</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Empresa ABC Ltda</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>12.345.678/0001-90</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>(11) 98765-4321</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>contato@empresaabc.com.br</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Comercial</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>01234-567</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>3550308</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>SP</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Centro</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Avenida Paulista</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>1000</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>01/06/2023</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Notebook HP</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>VIP, Corporativo</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>15/03/1985</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Maria Silva - Responsável pelas compras</td>
            </tr>
            <tr style={{ backgroundColor: '#f9f9f9' }}>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>João da Silva</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>123.456.789-00</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>(21) 99876-5432</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>joao.silva@email.com</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Pessoal</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>22222-333</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Rio de Janeiro</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>RJ</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Copacabana</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Avenida Atlântica</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>500</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Smartphone Samsung</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Premium</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>22/11/1990</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Maria Silva - Responsável pelas compras</td>
            </tr>
            <tr style={{ backgroundColor: '#e8f5e8' }}>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Maria Santos</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>(31) 99999-8888</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Belo Horizonte</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>Minas Gerais</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}></td>
            </tr>
          </tbody>
        </table>
      </div>

      {showResult && importResult && (
        <Alert
          message={importResult.hasErrors ? "Importação concluída com erros" : "Importação realizada com sucesso"}
          description={
            <div>
              <p style={{ marginBottom: '8px', fontWeight: 'bold' }}>{importResult.message}</p>
              {importResult.errors.length > 0 && (
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  <p style={{ marginBottom: '8px', fontWeight: 'bold' }}>Detalhes dos erros:</p>
                  <ul style={{ marginBottom: 0 }}>
                    {importResult.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          }
          type={importResult.hasErrors ? "warning" : "success"}
          showIcon
          closable
          onClose={handleCloseResult}
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" onClick={handleCloseResult}>
              {importResult.hasErrors ? "Fechar" : "Concluir"}
            </Button>
          }
        />
      )}

      {errors.length > 0 && !showResult && (
        <Alert
          message="Erros na importação"
          description={
            <div style={{ maxHeight: '200px', overflow: 'auto' }}>
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Campo para identificar clientes duplicados:</label>
          <Select
            value={searchField}
            onChange={setSearchField}
            style={{ width: 200 }}
            options={[
              { value: 'document', label: 'Documento (CPF/CNPJ)' },
              { value: 'email', label: 'Email' }
            ]}
          />
        </div>
        <Checkbox 
          checked={updateDuplicated}
          onChange={(e) => setUpdateDuplicated(e.target.checked)}
        >
          Atualizar clientes duplicados (se desmarcado, clientes duplicados serão ignorados)
        </Checkbox>
      </div>

      <Row align="middle" style={{ marginTop: 16 }}>
        <Col>
          <Upload
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false}
            accept=".xlsx"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Selecione o arquivo</Button>
          </Upload>
        </Col>
        <Col>
          <Tooltip title="Baixe um exemplo de planilha de importação">
            <DownloadOutlined 
              onClick={downloadExampleFile} 
              style={{ marginLeft: 16, fontSize: '24px', color: '#ffc107', cursor: 'pointer' }}
              spin={loadingExample}
            />
          </Tooltip>
        </Col>
      </Row>
    </Modal>
  );
};

export default CustomerImportModal;